import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OrderDetails.css';
import { getAuthToken } from '../utils/auth';

function OrderDetails({ warehouse, picker, startDate, endDate, country, orderType, excludeCarriers, apiUrl, onBack }) {
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'order_no', direction: 'asc' });

  const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [warehouse, picker, startDate, endDate, country, orderType, excludeCarriers]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (country && country !== 'All') params.append('country', country);
      if (orderType && orderType !== 'All') params.append('orderType', orderType);
      if (excludeCarriers) params.append('excludeCarriers', 'true');

      const headers = getAuthHeaders();
      const response = await axios.get(`${apiUrl}/order-details/${warehouse}/${encodeURIComponent(picker)}?${params}`, { headers });
      setOrders(response.data.orders);
      setTotals(response.data.totals);
      setError(null);
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null) return 1;
    if (bValue === null) return -1;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return sortConfig.direction === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⬍';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  if (loading) {
    return <div className="order-details-loading">Loading order details...</div>;
  }

  if (error) {
    return <div className="order-details-error">{error}</div>;
  }

  return (
    <div className="order-details-container">
      <div className="order-details-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Picker List
        </button>
        <h2>{picker} - Order Details</h2>
        <p className="date-range">
          Date Range: {startDate} - {endDate}
        </p>
        <p className="total-picks">Total Picks: {totals?.picks || 0}</p>
        <p className="total-picks">Avg Days to Pick: {totals?.avg_days_to_pick?.toFixed(2) || '0.00'}</p>
      </div>

      <div className="order-details-table-container">
        <table className="order-details-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('order_no')}>
                Order No{getSortIndicator('order_no')}
              </th>
              <th onClick={() => handleSort('invoice_no')}>
                Invoice Number{getSortIndicator('invoice_no')}
              </th>
              <th onClick={() => handleSort('days_to_pick')}>
                Days to Pick{getSortIndicator('days_to_pick')}
              </th>
              <th onClick={() => handleSort('lines')}>
                Lines{getSortIndicator('lines')}
              </th>
              <th onClick={() => handleSort('quantity')}>
                Quantity{getSortIndicator('quantity')}
              </th>
              <th onClick={() => handleSort('weight')}>
                Weight{getSortIndicator('weight')}
              </th>
              <th onClick={() => handleSort('dollars')}>
                Dollars{getSortIndicator('dollars')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map((order, index) => (
              <tr key={index}>
                <td>{order.order_no}</td>
                <td>{order.invoice_no}</td>
                <td>{order.days_to_pick || 0}</td>
                <td>{order.lines?.toLocaleString() || 0}</td>
                <td>{order.quantity?.toLocaleString() || 0}</td>
                <td>{order.weight?.toLocaleString() || 0} lbs</td>
                <td>${order.dollars?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="totals-row">
              <td><strong>Totals:</strong></td>
              <td><strong>Avg: {totals?.avg_days_to_pick?.toFixed(2) || '0.00'}</strong></td>
              <td><strong>{totals?.lines?.toLocaleString() || 0}</strong></td>
              <td><strong>{totals?.quantity?.toLocaleString() || 0}</strong></td>
              <td><strong>{totals?.weight?.toLocaleString() || 0} lbs</strong></td>
              <td><strong>${totals?.dollars?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default OrderDetails;
